/**
 * Pure utilities for the dynamic pricing feature.
 *
 * Covers:
 * - Price formatting with compact rounding rules (round-up)
 * - Currency symbol and contextual message lookup
 */

// ---------------------------------------------------------------------------
// Price formatting
// ---------------------------------------------------------------------------

/**
 * Formats a numeric price amount according to the dynamic pricing display rules.
 * All rounding is performed ceiling (round-up).
 *
 * Ranges and output shapes:
 *   0.01 – 999          → integer, e.g. "$40"
 *   999.01 – 9,999      → integer with comma, e.g. "$1,235"
 *   9,999.01 – 99,900   → 2 integer digits + 1 decimal + K, e.g. "11.2K"
 *   99,900.01 – 999,000 → 3 integer digits + K, e.g. "187K"
 *   999,000.01 – 9,990,000     → 1 digit + 2 decimals + M, e.g. "1.24M"
 *   9,990,000.01 – 99,900,000  → 2 digits + 1 decimal + M, e.g. "12.4M"
 *   99,900,000.01 – 999,000,000 → 3 digits + M, e.g. "387M"
 */
export function formatDynamicPrice(amount: number): string {
  if (amount <= 0) return '0'

  if (amount <= 999) {
    return Math.ceil(amount).toLocaleString('en-US')
  }

  if (amount <= 9_999) {
    return Math.ceil(amount).toLocaleString('en-US')
  }

  if (amount <= 99_900) {
    // Two integer digits + one decimal, e.g. 11.2K
    const k = amount / 1_000
    const rounded = ceilToDecimals(k, 1)
    return `${rounded.toFixed(1)}K`
  }

  if (amount <= 999_000) {
    // Three integer digits + K, no decimals, e.g. 187K
    const k = amount / 1_000
    return `${Math.ceil(k)}K`
  }

  if (amount <= 9_990_000) {
    // One digit + two decimals + M, e.g. 1.24M
    const m = amount / 1_000_000
    const rounded = ceilToDecimals(m, 2)
    return `${rounded.toFixed(2)}M`
  }

  if (amount <= 99_900_000) {
    // Two digits + one decimal + M, e.g. 12.4M
    const m = amount / 1_000_000
    const rounded = ceilToDecimals(m, 1)
    return `${rounded.toFixed(1)}M`
  }

  // Three digits + M, no decimals, e.g. 387M
  const m = amount / 1_000_000
  return `${Math.ceil(m)}M`
}

/** Rounds a number UP to the given number of decimal places. */
function ceilToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.ceil(value * factor) / factor
}

// ---------------------------------------------------------------------------
// Currency config
// ---------------------------------------------------------------------------

type CurrencyConfig = {
  /** Symbol to show beside the price on the time tile. Null means no symbol. */
  symbol: string | null
  /**
   * Prefix to use when formatting a standalone price (e.g. on the product card).
   * For currencies with a symbol this equals the symbol; for others it is the
   * currency code followed by a space (e.g. "AED ").
   */
  prefix: string
  /** Contextual message to show near the price. Null means no message needed. */
  messageIntlKey: string | null
}

const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  AED: { symbol: null, prefix: 'AED ', messageIntlKey: 'dynamic-pricing.message.AED' },
  ANG: { symbol: null, prefix: 'ANG ', messageIntlKey: 'dynamic-pricing.message.ANG' },
  AUD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.AUD' },
  AWG: { symbol: null, prefix: 'AWG ', messageIntlKey: 'dynamic-pricing.message.AWG' },
  BBD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.BBD' },
  BMD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.BMD' },
  BRL: { symbol: 'R$', prefix: 'R$', messageIntlKey: 'dynamic-pricing.message.BRL' },
  BSD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.BSD' },
  CAD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.CAD' },
  CHF: { symbol: null, prefix: 'CHF ', messageIntlKey: 'dynamic-pricing.message.CHF' },
  CLP: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.CLP' },
  CRC: { symbol: '₡', prefix: '₡', messageIntlKey: 'dynamic-pricing.message.CRC' },
  CZK: { symbol: null, prefix: 'Kč ', messageIntlKey: 'dynamic-pricing.message.CZK' },
  DOP: { symbol: null, prefix: 'RD$', messageIntlKey: 'dynamic-pricing.message.DOP' },
  EUR: { symbol: '€', prefix: '€', messageIntlKey: null },
  GBP: { symbol: '£', prefix: '£', messageIntlKey: null },
  GTQ: { symbol: null, prefix: 'Q', messageIntlKey: 'dynamic-pricing.message.GTQ' },
  JMD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.JMD' },
  JPY: { symbol: '¥', prefix: '¥', messageIntlKey: null },
  KYD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.KYD' },
  MXN: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.MXN' },
  NOK: { symbol: null, prefix: 'NOK ', messageIntlKey: 'dynamic-pricing.message.NOK' },
  NZD: { symbol: '$', prefix: '$', messageIntlKey: 'dynamic-pricing.message.NZD' },
  PLN: { symbol: null, prefix: 'zł', messageIntlKey: 'dynamic-pricing.message.PLN' },
  SEK: { symbol: null, prefix: 'SEK ', messageIntlKey: 'dynamic-pricing.message.SEK' },
  USD: { symbol: '$', prefix: '$', messageIntlKey: null }
}

/**
 * Returns the symbol to display alongside a price for the given currency.
 * Returns null when no symbol should be shown.
 */
export function getCurrencySymbol(currency: string): string | null {
  return CURRENCY_CONFIG[currency]?.symbol ?? null
}

/**
 * Returns the display prefix for a formatted standalone price string.
 * Uses the currency symbol when available, otherwise a currency code or local prefix.
 * Returns null for unknown currencies.
 */
export function getCurrencyPrefix(currency: string): string | null {
  return CURRENCY_CONFIG[currency]?.prefix ?? null
}

/**
 * Returns the contextual currency message for the given currency.
 * Returns null when no message is needed (e.g. USD, EUR, GBP).
 */
export function getCurrencyMessage(currency: string): string | null {
  return CURRENCY_CONFIG[currency]?.messageIntlKey ?? null
}

// ---------------------------------------------------------------------------
// Pricing entry helpers
// ---------------------------------------------------------------------------

/**
 * Returns the Money value to use for displaying a pricing entry.
 * Centralised so the field choice (listPrice vs price) can be changed in one place.
 */
export function getPriceMoney(pricing: AvailabilityTimePricing): Money {
  return pricing.listPrice
}

/**
 * Returns the candidate tickets for a slot, respecting the slot's whitelist.
 * Falls back to all selectable tickets when no whitelist is present.
 */
export function getCandidatesForSlot(slot: AvailabilityTime, selectableTickets: Ticket[]): Ticket[] {
  const whitelist = slot.whitelistedResourceOptionIds
  return whitelist?.length > 0
    ? selectableTickets.filter((t) => whitelist.includes(t.id))
    : [...selectableTickets]
}
